import type { SchrijvenVariantContext } from "@a2/core";
import { SCHRIJVEN_TASK_LABELS } from "@a2/core";
import { useT } from "../../lib/use-t";

interface SchrijvenGutterProps {
  variant: SchrijvenVariantContext;
  /** Bullet coverage state — one boolean per bullet, same index as variant.bullets. */
  bulletCovered?: boolean[];
  /** Minimum word target (static A2 floor) for the requirements footer. Omit to hide. */
  minWords?: number;
  /** Human-readable register label (e.g. "Formeel (u)"). Null/omit to hide the cell. */
  registerLabel?: string | null;
  /** Render plain (no card chrome) — used inside the mobile `<details>` body. */
  bare?: boolean;
}

/**
 * Opdracht card: task title, italic-serif scenario, a checkbox-style bullet
 * checklist (filled pass-green box + check glyph when covered), and a
 * requirements footer (Minimum / Register). Coverage data is the live
 * `useBulletCoverage` boolean[].
 */
export function SchrijvenGutter({
  variant,
  bulletCovered,
  minWords,
  registerLabel,
  bare = false,
}: SchrijvenGutterProps) {
  const t = useT();
  const taskLabel = SCHRIJVEN_TASK_LABELS[variant.taskType];
  const showReq = (minWords != null) || (registerLabel != null);

  const body = (
    <div className={bare ? "" : "px-5 py-4"}>
      <p className="font-serif-italic text-[16px] text-[var(--ink)] leading-relaxed mb-4">
        {variant.scenario}
      </p>

      {variant.bullets.length > 0 ? (
        <>
          <p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)] mb-2.5">
            {t("schrijven.runner.pointsLabel")}
          </p>
          <ul className="space-y-2.5 list-none p-0 m-0">
            {variant.bullets.map((bullet, i) => {
              const covered = bulletCovered?.[i] ?? false;
              return (
                <li
                  key={i}
                  className="flex items-start gap-2.5 text-sm leading-snug"
                >
                  <span
                    className="grid place-items-center w-[18px] h-[18px] rounded-[5px] shrink-0 mt-px text-white transition-colors duration-300"
                    style={{
                      background: covered ? "var(--pass)" : "transparent",
                      border: covered
                        ? "1.5px solid var(--pass)"
                        : "1.5px solid var(--border-strong)",
                    }}
                    aria-hidden
                  >
                    {covered ? (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    ) : null}
                  </span>
                  <span className={covered ? "text-[var(--ink)]" : "text-[var(--ink-muted)]"}>
                    {bullet}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {showReq ? (
        <div className="flex gap-3.5 mt-4 pt-4 border-t border-[var(--border)]">
          {minWords != null ? (
            <div className="flex-1">
              <div className="font-mono-badge text-[9.5px] uppercase tracking-wide text-[var(--ink-faint)]">
                {t("schrijven.runner.reqMinLabel")}
              </div>
              <div className="font-display font-semibold text-[18px] text-[var(--ink)] mt-0.5">
                {t("schrijven.runner.reqMinValueFmt", { count: minWords })}
              </div>
            </div>
          ) : null}
          {registerLabel != null ? (
            <div className="flex-1">
              <div className="font-mono-badge text-[9.5px] uppercase tracking-wide text-[var(--ink-faint)]">
                {t("schrijven.runner.reqRegisterLabel")}
              </div>
              <div className="font-display font-semibold text-[18px] text-[var(--ink)] mt-0.5">
                {registerLabel}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {variant.imageUrls.length > 0 ? (
        <div className="grid grid-cols-2 gap-2 mt-4">
          {variant.imageUrls.map((img, i) => (
            <div key={i} className="space-y-1">
              <img
                src={img.url}
                alt={img.captionWord}
                className="w-full aspect-square object-cover rounded-lg border border-[var(--border)]"
              />
              <p className="text-xs text-center text-[var(--ink-faint)] font-mono-badge">
                {img.captionWord}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  if (bare) return body;

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] shadow-[var(--shadow)] overflow-hidden">
      <div className="px-5 pt-4 pb-3.5 border-b border-[var(--border)]">
        <span className="block font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)] mb-2">
          {t("schrijven.runner.opdrachtEyebrow")}
        </span>
        <h2 className="font-display font-semibold text-[20px] text-[var(--ink)] leading-tight">
          {taskLabel}
        </h2>
      </div>
      {body}
    </div>
  );
}
